import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import {
  ArrowLeftRight, Wrench, PackageX, FileText, Star, Download,
  Eye, Pencil, Trash2, ChevronDown, ChevronUp, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TIPO_ICON = {
  troca: { icon: ArrowLeftRight, color: "#ff6b23" },
  manutencao: { icon: Wrench, color: "#dc2626" },
  devolucao: { icon: PackageX, color: "#2563eb" },
  aditivo: { icon: FileText, color: "#0f1923" },
  reclamacao: { icon: FileText, color: "#7c3aed" },
};
const TIPO_LABEL = { troca: "Troca", manutencao: "Manutenção", devolucao: "Devolução", aditivo: "Aditivo", reclamacao: "Reclamação" };
const STATUS_LABEL = {
  aberto: { label: "Aberto", color: "bg-blue-100 text-blue-700" },
  em_atendimento: { label: "Em andamento", color: "bg-yellow-100 text-yellow-800" },
  resolvido: { label: "Resolvido", color: "bg-green-100 text-green-700" },
  fechado: { label: "Fechado", color: "bg-gray-200 text-gray-700" },
  cancelado: { label: "Cancelado", color: "bg-red-100 text-red-700" },
};

// === RESUMO POR TIPO ===
function ResumoPorTipo({ tickets }) {
  const grupos = useMemo(() => {
    const acc = { troca: 0, devolucao: 0, aditivo: 0, manutencao: 0 };
    tickets.forEach((t) => { if (acc[t.tipo] !== undefined) acc[t.tipo]++; });
    return acc;
  }, [tickets]);
  return (
    <Card className="p-4" data-testid="resumo-por-tipo">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Resumo por tipo</h3>
      <ul className="space-y-2 text-sm">
        <ResumoLine icon={<ArrowLeftRight size={14} className="text-orange-600" />} label="Trocas"     value={grupos.troca}     />
        <ResumoLine icon={<PackageX size={14} className="text-blue-600" />}        label="Devoluções" value={grupos.devolucao} />
        <ResumoLine icon={<FileText size={14} className="text-slate-700" />}       label="Aditivos"   value={grupos.aditivo}   />
        <ResumoLine icon={<Wrench size={14} className="text-red-600" />}           label="Manutenções" value={grupos.manutencao} />
      </ul>
    </Card>
  );
}
const ResumoLine = ({ icon, label, value }) => (
  <li className="flex items-center justify-between py-1.5 border-b border-dashed border-gray-100 last:border-0">
    <span className="flex items-center gap-2 text-gray-700">{icon}{label}</span>
    <span className="font-semibold tabular-nums" style={{ color: "#0f1923" }}>{value}</span>
  </li>
);

// === CARD DE TICKET ABERTO (com Ver / Editar / Excluir) ===
function TicketAbertoCard({ t, onVer, onEditar, onCancelar, onAvaliar }) {
  const cfg = TIPO_ICON[t.tipo] || TIPO_ICON.aditivo;
  const Icon = cfg.icon;
  const status = STATUS_LABEL[t.status] || { label: t.status, color: "bg-gray-100 text-gray-700" };
  const editavel = t.status === "aberto";
  const token = localStorage.getItem("lab_token");
  const pdfUrl = t.pdf_url ? `${API}${t.pdf_url}${t.pdf_url.includes("?") ? "&" : "?"}auth=${token}` : null;
  return (
    <Card className="p-4 hover:shadow-md transition" data-testid={`portal-ticket-${t.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: cfg.color + "20", color: cfg.color }}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold" style={{ color: "#0f1923" }}>
                {TIPO_LABEL[t.tipo] || t.tipo} · <span className="text-gray-600 font-normal">{t.modelo_nome || "Aditivo de contrato"}</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                #{t.id.slice(0, 6).toUpperCase()}
                {t.unidade_serie ? ` · ${t.unidade_serie}` : ""}
                {" · "}{new Date(t.criado_em).toLocaleDateString("pt-BR")}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {t.urgencia_ia === "Alta" && <Badge className="bg-red-100 text-red-700">Urgência alta</Badge>}
              <Badge className={status.color}>{status.label}</Badge>
            </div>
          </div>

          {t.updates_recentes?.length > 0 && (
            <div className="mt-3 pl-3 border-l-2 border-orange-200 space-y-1">
              {t.updates_recentes.slice(0, 2).map((u) => (
                <div key={u.id} className="text-xs text-gray-600">
                  <span className="font-medium text-gray-700">{new Date(u.data).toLocaleDateString("pt-BR")}</span>
                  {" · "}{u.observacao || `Status: ${u.status_novo}`}
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => onVer(t)} data-testid={`ticket-ver-${t.id}`}>
              <Eye size={12} className="mr-1.5" /> Ver
            </Button>
            {editavel && (
              <>
                <Button size="sm" variant="outline" onClick={() => onEditar(t)} data-testid={`ticket-editar-${t.id}`}>
                  <Pencil size={12} className="mr-1.5" /> Editar
                </Button>
                <Button size="sm" variant="outline" onClick={() => onCancelar(t)} data-testid={`ticket-excluir-${t.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300">
                  <Trash2 size={12} className="mr-1.5" /> Excluir
                </Button>
              </>
            )}
            {pdfUrl && (
              <Button size="sm" variant="outline" onClick={() => window.open(pdfUrl, "_blank")} data-testid={`ticket-pdf-${t.id}`}>
                <Download size={12} className="mr-1.5" /> Ver PDF
              </Button>
            )}
            {(t.status === "resolvido" || t.status === "fechado") && !t.nps && (
              <Button size="sm" onClick={() => onAvaliar(t.id)} data-testid={`ticket-avaliar-${t.id}`} className="text-white" style={{ background: "#ff6b23" }}>
                <Star size={12} className="mr-1.5" /> Avaliar atendimento
              </Button>
            )}
            {t.nps && (
              <Badge variant="outline" className="border-green-300 text-green-700">
                NPS enviado: {t.nps.nota}/10
              </Badge>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

// === CARD DE TICKET FECHADO (somente leitura — Expandir) ===
function TicketFechadoCard({ t, onAvaliar }) {
  const [open, setOpen] = useState(false);
  const cfg = TIPO_ICON[t.tipo] || TIPO_ICON.aditivo;
  const Icon = cfg.icon;
  const status = STATUS_LABEL[t.status] || { label: t.status, color: "bg-gray-100 text-gray-700" };
  return (
    <Card className="p-4 transition" data-testid={`portal-ticket-fechado-${t.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: cfg.color + "20", color: cfg.color }}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold" style={{ color: "#0f1923" }}>
                {TIPO_LABEL[t.tipo] || t.tipo} · <span className="text-gray-600 font-normal">{t.modelo_nome || "Aditivo de contrato"}</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                #{t.id.slice(0, 6).toUpperCase()}{t.unidade_serie ? ` · ${t.unidade_serie}` : ""}
                {" · Aberto em "}{new Date(t.criado_em).toLocaleDateString("pt-BR")}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge className={status.color}>{status.label}</Badge>
              <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)} data-testid={`ticket-expand-${t.id}`}>
                {open ? <><ChevronUp size={12} className="mr-1" />Recolher</> : <><ChevronDown size={12} className="mr-1" />Expandir</>}
              </Button>
            </div>
          </div>

          {open && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Descrição original</div>
                <div className="text-gray-700 whitespace-pre-line">{t.descricao || "—"}</div>
              </div>
              {t.updates_recentes?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Histórico</div>
                  <ul className="space-y-1 pl-3 border-l-2 border-gray-200">
                    {t.updates_recentes.map((u) => (
                      <li key={u.id} className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">{new Date(u.data).toLocaleString("pt-BR")}</span>
                        {" · "}{u.observacao || `Status: ${u.status_novo}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap gap-3 text-xs text-gray-500 pt-1">
                <span>Tipo: <strong>{TIPO_LABEL[t.tipo] || t.tipo}</strong></span>
                {t.unidade_serie && <span>Equipamento: <strong>#{t.unidade_serie}</strong></span>}
                <span>Status final: <strong>{status.label}</strong></span>
              </div>
              {(t.status === "resolvido" || t.status === "fechado") && !t.nps && (
                <Button size="sm" onClick={() => onAvaliar(t.id)} className="text-white mt-2" style={{ background: "#ff6b23" }} data-testid={`ticket-fechado-avaliar-${t.id}`}>
                  <Star size={12} className="mr-1.5" /> Avaliar atendimento
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// === PÁGINA ===
export default function ClienteChamados() {
  const nav = useNavigate();
  const [abertos, setAbertos] = useState([]);
  const [finalizados, setFinalizados] = useState([]);
  const [verModal, setVerModal] = useState(null);
  const [editarModal, setEditarModal] = useState(null);
  const [editDesc, setEditDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/portal/tickets?status=abertos").then((r) => setAbertos(r.data.tickets || []));
    api.get("/portal/tickets?status=finalizados").then((r) => setFinalizados(r.data.tickets || []));
  };
  useEffect(() => {
    load();
    // Polling 30s — sincroniza status do ticket com o operador em tempo real
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleVer = async (t) => {
    setVerModal({ loading: true });
    try {
      const { data } = await api.get(`/portal/tickets/${t.id}`);
      setVerModal({ ticket: data.ticket, updates: data.updates });
    } catch { toast.error("Falha ao carregar chamado"); setVerModal(null); }
  };
  const handleEditar = (t) => { setEditarModal(t); setEditDesc(t.descricao || ""); };
  const salvarEdicao = async () => {
    if (!editDesc.trim()) { toast.error("Descrição não pode ficar vazia"); return; }
    setBusy(true);
    try {
      await api.put(`/portal/tickets/${editarModal.id}`, { descricao: editDesc.trim() });
      toast.success("Chamado atualizado");
      setEditarModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); }
    finally { setBusy(false); }
  };
  const handleCancelar = async (t) => {
    if (!window.confirm(`Cancelar o chamado #${t.id.slice(0, 6).toUpperCase()}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/portal/tickets/${t.id}`);
      toast.success("Chamado cancelado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao cancelar"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold" style={{ color: "#0f1923" }}>Meus chamados</h1>
          <p className="text-sm text-gray-500">Acompanhe suas solicitações em tempo real.</p>
        </div>
        <Button onClick={() => nav("/cliente/home")} data-testid="chamados-novo-btn" className="text-white" style={{ background: "#ff6b23" }}>
          Abrir novo chamado
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <div>
          <Tabs defaultValue="abertos">
            <TabsList data-testid="chamados-tabs">
              <TabsTrigger value="abertos" data-testid="chamados-tab-abertos">Abertos ({abertos.length})</TabsTrigger>
              <TabsTrigger value="finalizados" data-testid="chamados-tab-finalizados">Finalizados ({finalizados.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="abertos" className="space-y-3 mt-4">
              {abertos.length === 0 ? (
                <Card className="p-8 text-center text-sm text-gray-500" data-testid="chamados-abertos-vazio">
                  NÃO HÁ TICKETS ATIVOS NO MOMENTO!
                </Card>
              ) : abertos.map((t) => (
                <TicketAbertoCard
                  key={t.id} t={t}
                  onVer={handleVer}
                  onEditar={handleEditar}
                  onCancelar={handleCancelar}
                  onAvaliar={(id) => nav(`/pesquisas/ticket/${id}/responder`)}
                />
              ))}
            </TabsContent>
            <TabsContent value="finalizados" className="space-y-3 mt-4">
              {finalizados.length === 0 ? (
                <Card className="p-8 text-center text-sm text-gray-500">Nenhum chamado finalizado.</Card>
              ) : finalizados.map((t) => (
                <TicketFechadoCard key={t.id} t={t} onAvaliar={(id) => nav(`/pesquisas/ticket/${id}/responder`)} />
              ))}
            </TabsContent>
          </Tabs>
        </div>

        {/* Painel lateral — Resumo */}
        <aside className="space-y-3">
          <ResumoPorTipo tickets={[...abertos, ...finalizados]} />
        </aside>
      </div>

      {/* Modal Ver */}
      <Dialog open={!!verModal} onOpenChange={(o) => !o && setVerModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="modal-ver-ticket">
          <DialogHeader>
            <DialogTitle>Detalhes do chamado</DialogTitle>
            <DialogDescription>Informações completas e histórico do atendimento.</DialogDescription>
          </DialogHeader>
          {verModal?.loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14} /> Carregando…</div>
          ) : verModal?.ticket ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-gray-500">ID</div><div className="font-mono">#{verModal.ticket.id.slice(0, 6).toUpperCase()}</div></div>
                <div><div className="text-xs text-gray-500">Tipo</div><div>{TIPO_LABEL[verModal.ticket.tipo]}</div></div>
                <div><div className="text-xs text-gray-500">Status</div><Badge className={(STATUS_LABEL[verModal.ticket.status]||{}).color}>{(STATUS_LABEL[verModal.ticket.status]||{}).label}</Badge></div>
                <div><div className="text-xs text-gray-500">Aberto em</div><div>{new Date(verModal.ticket.criado_em).toLocaleString("pt-BR")}</div></div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Descrição</div>
                <div className="bg-slate-50 rounded p-3 whitespace-pre-line">{verModal.ticket.descricao || "—"}</div>
              </div>
              {verModal.updates?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Histórico</div>
                  <ul className="space-y-1 pl-3 border-l-2 border-orange-200">
                    {verModal.updates.map((u) => (
                      <li key={u.id} className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">{new Date(u.data).toLocaleString("pt-BR")}</span>
                        {" · "}{u.observacao || `Status: ${u.status_novo}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Modal Editar */}
      <Dialog open={!!editarModal} onOpenChange={(o) => !o && setEditarModal(null)}>
        <DialogContent data-testid="modal-editar-ticket">
          <DialogHeader>
            <DialogTitle>Editar chamado</DialogTitle>
            <DialogDescription>Atualize a descrição/observações do seu chamado. Somente em status "aberto".</DialogDescription>
          </DialogHeader>
          <Textarea rows={6} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} data-testid="input-editar-descricao" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditarModal(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={salvarEdicao} disabled={busy} data-testid="btn-salvar-edicao" className="text-white" style={{ background: "#ff6b23" }}>
              {busy && <Loader2 className="animate-spin mr-2" size={14} />}Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
